import { createSelector } from 'reselect';
// Import state types.
import {
    AdministratorState,
    AppState,
    EligibilityMessagePrioritiesState,
} from '@app/redux/states';
// Import api models.
import {
    EligibilityMessagePriority,
    EligibilityMessagePriorityWithId,
} from '@app/models/api';
// Import base selector
import { getAdministratorState } from '@app/redux/selectors/base';

const getEligibilityMessagePrioritiesState = (state: AppState): EligibilityMessagePrioritiesState | undefined => {
    const administratorState: AdministratorState | undefined = getAdministratorState(state);
    return administratorState !== undefined
        ? administratorState.eligibilityMessagePrioritiesState
        : undefined;
};

const makeGetEligibilityMessagePrioritiesSelector = () => createSelector(
    [getEligibilityMessagePrioritiesState],
    (
        eligibilityMessagePrioritiesState: EligibilityMessagePrioritiesState | undefined
    ): EligibilityMessagePriorityWithId[] | undefined => {
        return eligibilityMessagePrioritiesState?.eligibilityMessagePriorities;
    }
);

const makeAreEligibilityMessagePrioritiesLoadedSelector = () => createSelector(
    [getEligibilityMessagePrioritiesState],
    (
        eligibilityMessagePrioritiesState: EligibilityMessagePrioritiesState | undefined
    ): boolean => {
        return eligibilityMessagePrioritiesState !== undefined
            ? eligibilityMessagePrioritiesState.areEligibilityMessagePrioritiesLoaded
            : false;
    }
);

const makeGetSelectedEligibilityMessagePriorityIdSelector = () => createSelector(
    [getEligibilityMessagePrioritiesState],
    (
        eligibilityMessagePrioritiesState: EligibilityMessagePrioritiesState | undefined
    ): string | undefined => {
        return eligibilityMessagePrioritiesState?.selectedEligibilityMessagePriorityId;
    }
);

const makeGetSelectedEligibilityMessagePrioritySelector = () => createSelector(
    [
        makeGetSelectedEligibilityMessagePriorityIdSelector(),
        makeGetEligibilityMessagePrioritiesSelector(),
    ],
    (
        selectedEligibilityMessagePriorityId: string | undefined,
        eligibilityMessagePriorities: EligibilityMessagePriorityWithId[] | undefined
    ): EligibilityMessagePriorityWithId | undefined => {
        return !String.isNullOrWhiteSpace(selectedEligibilityMessagePriorityId) 
            ? eligibilityMessagePriorities?.find((eligibilityMessagePriority: EligibilityMessagePriorityWithId) => eligibilityMessagePriority.id === selectedEligibilityMessagePriorityId)
            : undefined;
    }
);

const makeGetSelectedEligibilityMessagePriorityRequestSelector = () => createSelector(
    [makeGetSelectedEligibilityMessagePrioritySelector()],
    ( selectedEligibilityMessagePriority: EligibilityMessagePriorityWithId | undefined): EligibilityMessagePriority | undefined => {
        let selectedEligibilityMessagePriorityRequest: EligibilityMessagePriority | undefined = undefined;
        if (selectedEligibilityMessagePriority !== undefined) {
            selectedEligibilityMessagePriorityRequest = new EligibilityMessagePriority();
            selectedEligibilityMessagePriorityRequest.eligibilityName = selectedEligibilityMessagePriority?.eligibilityName ?? null;
            selectedEligibilityMessagePriorityRequest.messageOverride = selectedEligibilityMessagePriority?.messageOverride ?? null;
            selectedEligibilityMessagePriorityRequest.priority = selectedEligibilityMessagePriority?.priority ?? null;
        }
        return selectedEligibilityMessagePriorityRequest;
    }
);

// Export composed methods.
export { getEligibilityMessagePrioritiesState };

// Export selectors.
export {
    makeAreEligibilityMessagePrioritiesLoadedSelector,
    makeGetEligibilityMessagePrioritiesSelector,
    makeGetSelectedEligibilityMessagePriorityIdSelector,
    makeGetSelectedEligibilityMessagePriorityRequestSelector,
    makeGetSelectedEligibilityMessagePrioritySelector,
};
